// Admin Users (CMS operators) API — wired to real backend.
//
// Backend paths (ISSUE-44 — full CRUD now available):
//   GET    /api/v1/admin/admin-users
//   GET    /api/v1/admin/admin-users/{id}
//   POST   /api/v1/admin/admin-users                       { email, firstName, lastName, password, ... }
//   PUT    /api/v1/admin/admin-users/{id}                  { email, firstName, lastName, phoneNumber?, avatarUrl? }
//   DELETE /api/v1/admin/admin-users/{id}
//   POST   /api/v1/admin/admin-users/{id}/assign-role      { roleName }
//   POST   /api/v1/admin/admin-users/{id}/revoke-role      { roleName }
//   POST   /api/v1/admin/app-users/{id}/reset-password     (reused for admins; same identity store)
//
// Note: full CRUD shipped in BE ISSUE-44. The legacy NormalizedApiError shims
// were removed once create/update/delete became real endpoints.

import { http, unwrap, unwrapList, type ActionResponse } from './http';
import i18n from '@/i18n';
import type { AdminUser, AdminUserStatus } from '@features/auth/types';
import type { Role } from '@shared/rbac/permissions.const';
import type {
  AdminUserListQuery,
  AdminUserListResponse,
  CreateAdminUserInput,
  UpdateAdminUserInput,
} from '@features/users/types';

interface UserDto {
  id: string;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  phoneNumber: string | null;
  memberType: string | null;
  provider: string | null;
  roles: string[] | null;
  isBanned: boolean;
  banReason: string | null;
  bannedAt: string | null;
  bannedUntil: string | null;
  lastLoginAt: string | null;
  avatarUrl: string | null;
  emailConfirmed: boolean;
  phoneNumberConfirmed: boolean;
  createdAt: string;
  updatedAt: string;
}

const BASE = '/api/v1/admin/admin-users';

/** Map a backend BE role name → the legacy three-tier FE `Role` enum. */
function feRoleFromBe(roles: string[] | null | undefined): Role {
  if (!roles || roles.length === 0) return 'WRITER';
  const flat = roles.map((r) => r.toLowerCase());
  if (flat.some((r) => r.includes('admin'))) return 'ADMIN';
  if (flat.some((r) => r.includes('edit'))) return 'EDITOR';
  return 'WRITER';
}

function feStatusFromBe(isBanned: boolean | undefined): AdminUserStatus {
  return isBanned ? 'inactive' : 'active';
}

function fullNameFromBe(dto: UserDto): string {
  const parts = [dto.firstName, dto.lastName].filter(Boolean).join(' ').trim();
  return parts || dto.email || i18n.t('common:fallback.unnamedUser');
}

function mapUser(dto: UserDto): AdminUser {
  return {
    id: dto.id,
    email: dto.email ?? '',
    fullName: fullNameFromBe(dto),
    role: feRoleFromBe(dto.roles),
    status: feStatusFromBe(dto.isBanned),
    avatarUrl: dto.avatarUrl ?? undefined,
    lastLoginAt: dto.lastLoginAt ?? undefined,
    createdAt: dto.createdAt,
    firstName: dto.firstName ?? undefined,
    lastName: dto.lastName ?? undefined,
    memberType: dto.memberType ?? undefined,
    roles: dto.roles ?? undefined,
  };
}

/** Split "Ad Soyad" into firstName / lastName (last token = lastName). */
function splitFullName(name: string | undefined): {
  firstName: string;
  lastName: string;
} {
  const tokens = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return { firstName: '', lastName: '' };
  if (tokens.length === 1) return { firstName: tokens[0] ?? '', lastName: '' };
  const last = tokens[tokens.length - 1] ?? '';
  const first = tokens.slice(0, -1).join(' ');
  return { firstName: first, lastName: last };
}

export const adminUsersApi = {
  async list(query: AdminUserListQuery = {}): Promise<AdminUserListResponse> {
    const params: Record<string, string | number | boolean> = {
      page: query.page ?? 1,
      pageSize: query.pageSize ?? 20,
    };
    if (query.q) params.search = query.q;
    if (query.role && query.role !== 'ALL') params.roleName = query.role;
    if (query.status && query.status !== 'ALL') {
      params.isBanned = query.status === 'inactive';
    }
    const res = await http.get<ActionResponse<UserDto>>(BASE, { params });
    const { items, pagination } = unwrapList(res);
    return {
      items: items.map(mapUser),
      total: pagination?.totalCount ?? items.length,
      page: pagination?.page ?? (query.page ?? 1),
      pageSize: pagination?.pageSize ?? (query.pageSize ?? 20),
      totalPages: pagination?.totalPages ?? 1,
    };
  },

  async get(id: string): Promise<AdminUser> {
    const dto = unwrap(await http.get<ActionResponse<UserDto>>(`${BASE}/${id}`));
    return mapUser(dto);
  },

  /**
   * ISSUE-44 — Create a new admin (operator) user. `fullName` is split into
   * firstName/lastName; the trailing token becomes lastName. Roles are optional
   * — the BE assigns them in one shot via the `roleNames` array.
   */
  async create(input: CreateAdminUserInput & { roleNames?: string[] }): Promise<AdminUser> {
    const { firstName, lastName } = splitFullName(input.fullName);
    const body = {
      email: input.email.trim(),
      firstName,
      lastName,
      password: input.password,
      mustResetPassword: true,
      roleNames: input.roleNames && input.roleNames.length > 0
        ? input.roleNames
        : undefined,
    };
    const dto = unwrap(
      await http.post<ActionResponse<UserDto>>(BASE, body),
    );
    return mapUser(dto);
  },

  /**
   * ISSUE-44 — Update an admin user's profile fields (email, name, phone).
   * Also accepts `resetPassword` for backwards compatibility — when present,
   * it issues a reset-password call against the shared identity endpoint.
   */
  async update(
    id: string,
    input: UpdateAdminUserInput & {
      email?: string;
      phoneNumber?: string;
      avatarUrl?: string;
    },
  ): Promise<AdminUser> {
    // Fetch current to fill in required fields that weren't provided.
    const current = await this.get(id);

    if (
      input.fullName !== undefined ||
      input.email !== undefined ||
      input.phoneNumber !== undefined ||
      input.avatarUrl !== undefined
    ) {
      const { firstName, lastName } = splitFullName(
        input.fullName ?? current.fullName,
      );
      const body = {
        email: input.email ?? current.email,
        firstName,
        lastName,
        phoneNumber: input.phoneNumber ?? null,
        avatarUrl: input.avatarUrl ?? current.avatarUrl ?? null,
      };
      await http.put<ActionResponse<UserDto>>(`${BASE}/${id}`, body);
    }

    if (input.resetPassword) {
      await http.post<ActionResponse<boolean>>(
        `/api/v1/admin/app-users/${id}/reset-password`,
        { newPassword: input.resetPassword },
      );
    }
    return this.get(id);
  },

  /** ISSUE-44 — Soft-delete an admin user. */
  async remove(id: string): Promise<void> {
    await http.delete<ActionResponse<boolean>>(`${BASE}/${id}`);
  },

  async assignRole(id: string, roleName: string): Promise<void> {
    await http.post<ActionResponse<boolean>>(`${BASE}/${id}/assign-role`, {
      roleName,
    });
  },

  async revokeRole(id: string, roleName: string): Promise<void> {
    await http.post<ActionResponse<boolean>>(`${BASE}/${id}/revoke-role`, {
      roleName,
    });
  },

  /**
   * MOD-04 / BIZ-SEC-04 — bir kullanıcının 2FA'sını sıfırla (security.manage).
   * settings.manage'den ayrı yetki; kullanıcı cihazını kaybettiğinde erişimi
   * geri açar (sonraki girişte 2FA yeniden kurulur).
   */
  async resetTwoFactor(id: string): Promise<void> {
    await http.delete<ActionResponse<boolean>>(`${BASE}/${id}/2fa`);
  },
};

export type AdminUsersApi = typeof adminUsersApi;
